import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle2, Sparkles, Lock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { calculateBundlePrice } from '@/data/mockData';
import { useApp } from '@/context/AppContext';
import apiService from '@/services/apiService';

const STORAGE_KEY = 'pending_document_id';
const SINGLE_PRICE = 10;

const PaymentModal = ({ onClose, pendingId, bundleCount = 1 }) => {
  const navigate = useNavigate();
  const { stripeMockMode } = useApp();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [error, setError] = useState(null);

  const bundlePricing = bundleCount > 1 ? calculateBundlePrice(bundleCount) : null;
  const displayPrice = bundlePricing ? bundlePricing.finalPrice : SINGLE_PRICE;

  const handlePayment = useCallback(async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Ensure pending ID is stored
      if (pendingId) {
        try {
          localStorage.setItem(STORAGE_KEY, pendingId);
        } catch (e) {
          // localStorage may be unavailable
        }
      }

      if (stripeMockMode) {
        // MOCK MODE: Simulate payment and process document directly
        setProcessingStep('Processing payment...');
        await new Promise(r => setTimeout(r, 1000));
        
        setProcessingStep('Analyzing document...');
        
        // Call the mock payment processing endpoint
        const result = await apiService.processPendingDocumentMock(pendingId);
        
        setProcessingStep('Preparing results...');
        await new Promise(r => setTimeout(r, 500));
        
        // Clear pending ID
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}
        
        // Navigate directly to results
        navigate('/results', {
          state: {
            analysis: result.analysis,
            isRealData: true,
            isPaid: true
          },
          replace: true
        });
      } else {
        // REAL MODE: Redirect to Stripe
        setProcessingStep('Connecting to Stripe...');
        
        const session = await apiService.createCheckoutSession(
          bundleCount > 1 ? null : 'single',
          bundleCount
        );
        
        if (session.checkout_url) {
          window.location.href = session.checkout_url;
        } else {
          throw new Error('Payment setup failed. Please try again.');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      setProcessingStep('');
    }
  }, [isProcessing, pendingId, bundleCount, stripeMockMode, navigate]);

  const handleClose = useCallback(() => {
    if (!isProcessing) {
      onClose();
    }
  }, [isProcessing, onClose]);

  const features = [
    'AI-powered document type detection',
    'Full readiness score with detailed breakdown',
    'Critical issues and warnings identified',
    'Step-by-step fix instructions',
    'Privacy protection - PII automatically redacted'
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm" 
      data-testid="payment-modal"
      onClick={handleClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <h2 className="font-outfit font-bold text-2xl text-slate-900">
            {bundlePricing ? `Analyze ${bundleCount} Documents` : 'Analyze Document'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
            data-testid="close-modal-button"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6">
          {/* Mock Mode Indicator */}
          {stripeMockMode && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-center">
              <strong>Test Mode:</strong> Payment is simulated (no real charge)
            </div>
          )}

          <p className="font-public text-slate-600 mb-8 text-center">
            {stripeMockMode 
              ? 'Click below to analyze your document (test mode)' 
              : 'Complete payment to receive your detailed document analysis'
            }
          </p>

          {/* Pricing Display */}
          <div className="bg-gradient-to-r from-trust/5 to-verify/5 border-2 border-trust/20 rounded-lg p-8 mb-6">
            <div className="text-center">
              {bundlePricing ? (
                <>
                  <p className="text-sm font-public text-slate-600 mb-2">Bundle Pricing</p>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="text-5xl font-outfit font-bold text-trust">${bundlePricing.finalPrice}</span>
                    <span className="text-2xl text-slate-400 line-through">${bundlePricing.totalWithoutDiscount}</span>
                  </div>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <span className="px-3 py-1 bg-verify text-verify-foreground rounded-md font-mono text-sm">
                      Save ${bundlePricing.savings}
                    </span>
                    <span className="px-3 py-1 bg-trust/10 text-trust rounded-md font-mono text-sm">
                      {bundlePricing.discount}% off
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    ${bundlePricing.perDocumentPrice} per document • {bundlePricing.documentCount} documents
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-public text-slate-600 mb-2">Single Document Analysis</p>
                  <div className="mb-3">
                    <span className="text-5xl font-outfit font-bold text-trust">${SINGLE_PRICE}</span>
                  </div>
                  <p className="text-sm text-slate-600">Complete analysis with fix instructions</p>
                </>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="font-public font-semibold text-slate-900 mb-3">What's Included:</h3>
            <ul className="space-y-2">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5 flex-shrink-0" />
                  <span className="font-public text-sm text-slate-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm" role="alert">
              {error}
            </div>
          )}

          {/* Payment Button */}
          <div className="text-center">
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className={`bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2 ${
                isProcessing ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              data-testid="payment-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {processingStep || 'Processing...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {stripeMockMode ? `Analyze Now (Test)` : `Pay $${displayPrice} - Analyze Now`}
                </>
              )}
            </button>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 mt-4 text-slate-500">
            <Lock className="w-4 h-4" />
            <span className="text-xs font-public">
              {stripeMockMode ? 'Test mode - no payment required' : 'Secure payment powered by Stripe'}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentModal;
